import { useGT } from 'gt-react';
import type { WorkbookDefinition } from '@erp/shared-types';
import type { ToolCall } from '@/context/agent-state.context';

type Args = Record<string, unknown>;

function record(value: unknown): Args | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value))
    : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** The name of a file, source, worksheet or output, or the title of an instruction. */
function nameOf(
  definition: WorkbookDefinition,
  id: unknown,
): string | undefined {
  if (typeof id !== 'string') return undefined;
  const items = [
    ...definition.files,
    ...definition.sources,
    ...definition.sheets,
    ...definition.outputs,
  ];
  return (
    items.find((item) => item.id === id)?.name ??
    text(definition.processSteps.find((step) => step.id === id)?.title)
  );
}

/** A worksheet the agent names by id or by name, as the name to show. */
function sheetName(definition: WorkbookDefinition, sheet: unknown) {
  return nameOf(definition, sheet) ?? text(sheet);
}

/** The name in a source the agent is still writing as JSON, if it has got that far. */
function sourceJsonName(sourceJson: unknown): string | undefined {
  if (typeof sourceJson !== 'string') return undefined;
  try {
    return text(record(JSON.parse(sourceJson))?.name);
  } catch {
    return undefined;
  }
}

export interface EditedItem {
  op: 'add' | 'edit' | 'remove';
  collection: string;
  name?: string;
}

/**
 * What the last operation of an `edit_workbook` call does to which item, and
 * how many operations it has. Null for one that changes only the workbook's
 * own settings or has none yet (the agent is still writing it).
 */
export function operationEdit(
  operation: Args,
  collection: string,
  definition: WorkbookDefinition,
): EditedItem | null {
  if (operation.op === 'remove')
    return { op: 'remove', collection, name: nameOf(definition, operation.id) };
  if (operation.op === 'update')
    return {
      op: 'edit',
      collection,
      name:
        nameOf(definition, operation.id) ??
        text(record(operation.changes)?.name),
    };
  if (operation.op !== 'upsert') return null;
  const item = record(operation.item);
  return {
    op: nameOf(definition, item?.id) ? 'edit' : 'add',
    collection,
    name: text(item?.name) ?? nameOf(definition, item?.id),
  };
}

function editedItem(
  args: Args,
  definition: WorkbookDefinition,
): { count: number; last: EditedItem | null } {
  const operations = Array.isArray(args.operations) ? args.operations : [];
  let last: EditedItem | null = null;
  for (const entry of operations) {
    const operation = record(entry);
    const collection = text(operation?.collection);
    if (!operation || !collection) continue;
    last = operationEdit(operation, collection, definition) ?? last;
  }
  return { count: operations.length, last };
}

/**
 * A specific, translated label for what a workbook frontend tool is doing,
 * naming its target where its arguments give one; null for any other tool.
 * Arguments may be partial while the call is still being written.
 */
export function useWorkbookToolLabel(
  definition: WorkbookDefinition,
): (call: ToolCall) => string | null {
  const gt = useGT();
  const kinds: Record<string, string> = {
    sources: gt('source', { context: 'workbook item kind, lower case' }),
    sheets: gt('worksheet', { context: 'workbook item kind, lower case' }),
    outputs: gt('output', { context: 'workbook item kind, lower case' }),
    processSteps: gt('instruction', {
      context: 'workbook item kind, lower case',
    }),
    taskRules: gt('task rule', { context: 'workbook item kind, lower case' }),
    files: gt('file', { context: 'workbook item kind, lower case' }),
  };
  const context = 'workbook generation status';
  function edit(args: Args): string | null {
    const { count, last } = editedItem(args, definition);
    if (count > 1)
      return gt('Applying {count} changes to the workbook…', {
        context,
        count,
      });
    if (!last) return null;
    const kind = kinds[last.collection] ?? last.collection;
    const name = last.name ?? '';
    return name ? namedEdit(last.op, kind, name) : unnamedEdit(last.op, kind);
  }
  function unnamedEdit(op: string, kind: string): string {
    if (op === 'remove') return gt('Removing a {kind}…', { context, kind });
    return op === 'add'
      ? gt('Adding a {kind}…', { context, kind })
      : gt('Editing a {kind}…', { context, kind });
  }
  function namedEdit(op: string, kind: string, name: string): string {
    if (op === 'add')
      return gt('Adding {kind} ‘{name}’…', { context, kind, name });
    if (op === 'remove')
      return gt('Removing {kind} ‘{name}’…', { context, kind, name });
    return gt('Editing {kind} ‘{name}’…', { context, kind, name });
  }
  function profile(args: Args): string | null {
    const columns = Array.isArray(args.columns) ? args.columns.length : 0;
    if (columns)
      return gt('Profiling {count} columns', { context, count: columns });
    const pairs = Array.isArray(args.pairs) ? args.pairs.length : 0;
    if (pairs)
      return gt('Checking how {count} keys match', { context, count: pairs });
    return gt('Profiling the data', { context });
  }
  const labels: Record<string, (args: Args) => string | null> = {
    inspect_workbook_file: (args) => {
      const name = nameOf(definition, args.fileId);
      return name ? gt('Reading file ‘{name}’…', { context, name }) : null;
    },
    preview_workbook_source: (args) => {
      const name = sourceJsonName(args.sourceJson);
      return name ? gt('Previewing source ‘{name}’…', { context, name }) : null;
    },
    read_worksheet_result: (args) => {
      const name = nameOf(definition, args.sheetId);
      return name
        ? gt('Reading the result of ‘{name}’…', { context, name })
        : gt('Reading a worksheet result…', { context });
    },
    preview_workbook_change: (args) => {
      const name = sheetName(definition, args.sheet);
      return name
        ? gt('Trying a change on ‘{name}’…', { context, name })
        : null;
    },
    workbook_object_catalog: () =>
      gt('Looking up query-builder fields…', { context }),
    workbook_automations: () =>
      gt('Checking what automates this workbook…', { context }),
    workbook_output_link: (args) => {
      const name = nameOf(definition, args.outputId);
      return name
        ? gt('Preparing a download of ‘{name}’…', { context, name })
        : gt('Preparing a download…', { context });
    },
    report_component_catalog: () =>
      gt('Looking up report components…', { context }),
    app_view_catalog: () => gt('Looking up App components…', { context }),
    build_app: (args) => {
      const name = nameOf(definition, args.outputId);
      return name
        ? gt('Building the App for ‘{name}’…', { context, name })
        : gt('Building the App…', { context });
    },
    create_stateful_table: (args) => {
      const name = text(args.name);
      return name
        ? gt('Creating the Stateful table ‘{name}’…', { context, name })
        : gt('Creating a Stateful table…', { context });
    },
    profile_workbook_data: profile,
    run_workbook_sample: () =>
      gt('Running the workbook on a sample…', { context }),
    propose_worksheet_check: (args) => {
      const name = nameOf(definition, args.sheetId);
      return name
        ? gt('Proposing a check for ‘{name}’…', { context, name })
        : gt('Proposing a worksheet check…', { context });
    },
    edit_workbook: edit,
    run_open_workbook: () => gt('Running the workbook…', { context }),
    open_run_tasks: () => gt('Opening tasks for the run…', { context }),
    create_instruction_task: (args) => {
      const name = nameOf(definition, args.stepId);
      return name
        ? gt('Creating a task for ‘{name}’…', { context, name })
        : gt('Creating a task for an instruction…', { context });
    },
    get_workbook_view: () => gt('Checking which tab you are on…', { context }),
    open_workbook_item: (args) => {
      const name = nameOf(definition, args.id) ?? text(args.id);
      return name
        ? gt('Opening ‘{name}’…', { context, name })
        : gt('Opening a tab…', { context });
    },
    workbook_sop_updates: () => gt('Reading the SOP updates…', { context }),
    regenerate_instructions: (args) => {
      const ids = Array.isArray(args.stepIds) ? args.stepIds : [];
      const [only] = ids;
      const name = ids.length === 1 ? nameOf(definition, only) : undefined;
      if (name)
        return gt('Regenerating instruction ‘{name}’…', { context, name });
      return ids.length
        ? gt('Regenerating {count} instructions…', {
            context,
            count: ids.length,
          })
        : gt('Regenerating instructions…', { context });
    },
    resolve_sop_update: (args) => {
      const name = nameOf(definition, args.stepId);
      const accept = args.resolution === 'accept';
      const discard = args.resolution === 'discard';
      if (!name) {
        if (accept) return gt('Accepting an SOP update…', { context });
        return discard
          ? gt('Discarding an SOP update…', { context })
          : gt('Resolving an SOP update…', { context });
      }
      if (!accept && !discard)
        return gt('Resolving the SOP update for ‘{name}’…', { context, name });
      return accept
        ? gt('Accepting the SOP update for ‘{name}’…', { context, name })
        : gt('Discarding the SOP update for ‘{name}’…', { context, name });
    },
  };
  return (call) => labels[call.toolName]?.(call.args ?? {}) ?? null;
}
